// View model for the "not eligible" banner: which check of the flow stopped the comp and the
// numbers behind it. Amounts stay exact (integer cents).
import { formatAmount, decimalCents, centsText } from './format';
import { formatShortUTC } from './timeline';

export const TOTAL_CHECKS = 5;

// formatMoney: "$4,255.73" for USD, "74.27 EUR" for everything else.
export const formatMoney = (amount, currency) =>
  (currency === 'USD' ? `$${formatAmount(amount)}` : formatAmount(amount, currency));

const usd = (cents) => formatMoney(centsText(cents), 'USD');

// pct of part in whole, 2 decimals, clamped to 0–100.
const pct = (part, whole) => (whole > 0 ? Math.round(Math.min(1, Math.max(0, part / whole)) * 10000) / 100 : 0);

function periodView() {
  return {
    step: 1,
    icon: 'period',
    title: 'No active period',
    text: 'The player has no open period in the data, so there is nothing to measure deposits against.',
    metrics: null,
    bar: null,
  };
}

function depositsView(check) {
  const d = check.deposits;
  const measured = decimalCents(d.measured || '0');
  const required = decimalCents(d.eligible_min);
  const missing = formatMoney(centsText(Math.max(0, required - measured)), d.basis_currency);
  const deadline = check.period?.period?.period_end;
  const share = pct(measured, required);
  return {
    step: 2,
    icon: 'deposits',
    title: 'Not enough deposited this period',
    text: `The player needs ${missing} more in deposits${deadline ? ` before ${formatShortUTC(deadline)} UTC` : ''} to qualify for a comp.`,
    metrics: [
      { label: 'Deposited this period', value: formatMoney(d.measured || '0.00', d.basis_currency) },
      { label: 'Required', value: formatMoney(d.eligible_min, d.basis_currency) },
      { label: 'Still missing', value: missing, accent: true },
    ],
    bar: { kind: 'progress', pct: share, left: `${Math.floor(share)}% of requirement`, right: `${formatMoney(d.eligible_min, d.basis_currency)} target` },
  };
}

function balanceView(check) {
  const { total_usd_cents: total, limit_usd_cents: limit } = check.balance;
  return {
    step: 3,
    icon: 'balance',
    title: 'Balance is still too high',
    text: `Ask the player to come back once their balance is below ${usd(limit)}. No comp until then.`,
    metrics: [
      { label: 'Current balance', value: usd(total) },
      { label: 'Must be below', value: usd(limit) },
      { label: 'Over the limit by', value: usd(total - limit), accent: true },
    ],
    bar: { kind: 'limit', pct: pct(limit, total), left: '$0', limit: `$${limit / 100} limit`, right: `${usd(total)} now` },
  };
}

function vipView() {
  return {
    step: 4,
    icon: 'vip',
    title: 'Not a VIP',
    text: 'VIP comps are for VIP players only. You can still issue it anyway, with a comment.',
    metrics: null,
    bar: null,
  };
}

// notEligibleView describes the first failing check, or null when the decision is not
// "not eligible" or no step failed.
export const notEligibleView = (check) => {
  if (check.decision !== 'not_eligible') return null;
  if (check.period?.status === 'fail') return periodView();
  if (check.deposits?.status === 'fail') return depositsView(check);
  if (check.balance?.status === 'fail') return balanceView(check);
  if (check.vip?.status === 'fail') return vipView();
  return null;
};

// checkChips: one chip per check in the flow's order; checks after the failing one did not run.
export const checkChips = (check) => {
  const chip = (label, status) => ({ label: status === 'skipped' ? `${label} · not run` : label, status });
  return [
    chip('Active period', check.period?.status),
    chip('Deposited enough', check.deposits?.status),
    chip('Balance below $20', check.balance?.status),
    chip('VIP player', check.vip?.status),
    chip('Comp amount', check.proposal ? 'pass' : 'skipped'),
  ];
};
